import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useColors } from '../../../../../theme/ColorModeContext';
import { StageSection, Prose, FactGrid, NumberedFlow, Callout, CodeSample, MiniLabel, Panel, SegmentedToggle } from '../../ContentPrimitives';
import { StageBlock, content, question } from '../../types';
import { CantonParty, creationShapes, missingSignatories } from './cantonModel';
import { PartyChip, PartyPicker, Verdict } from './cantonVisuals';

export const meta = {
    title: 'Contracts & Parties',
    tagline: 'Agreements between named parties, not balances or unspent outputs.',
};

const RoleTable = () => {
    const colors = useColors();
    const rows: [string, string, string, string][] = [
        ['Signatory', 'Yes, always', 'Yes', 'Bound by it. Can archive it.'],
        ['Observer', 'No', 'Yes', 'Can see it. Takes on no obligation.'],
        ['Anyone else', 'No', 'No', 'Never receives it at all.'],
    ];
    const cell = { p: '10px 12px', borderBottom: `1px solid ${colors.border.subtle}`, fontSize: '0.9rem' };
    return (
        <Box sx={{ overflowX: 'auto', my: 2.5 }}>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', minWidth: 480 }}>
                <thead>
                    <tr>
                        {['Role', 'Must authorize creation?', 'Sees the contract?', 'What it means'].map((header) => (
                            <Box component="th" key={header} sx={{ ...cell, color: colors.text.muted, textAlign: 'left', fontSize: '0.74rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                {header}
                            </Box>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map(([role, ...rest]) => (
                        <tr key={role}>
                            <Box component="td" sx={{ ...cell, color: colors.text.primary, fontWeight: 800 }}>{role}</Box>
                            {rest.map((value, index) => (
                                <Box component="td" key={index} sx={{ ...cell, color: colors.text.secondary }}>{value}</Box>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </Box>
        </Box>
    );
};

const CreationDemo = () => {
    const colors = useColors();
    const [shapeIndex, setShapeIndex] = useState('0');
    const [authorizers, setAuthorizers] = useState<CantonParty[]>(['Bank']);
    const shape = creationShapes[Number(shapeIndex)];
    const missing = missingSignatories(shape, authorizers);
    const created = missing.length === 0;

    const toggle = (party: CantonParty) => setAuthorizers((current) =>
        current.includes(party) ? current.filter((item) => item !== party) : [...current, party]);

    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: '18px' }}>
            <Panel>
                <MiniLabel>1. Pick a contract to create</MiniLabel>
                <SegmentedToggle<string>
                    ariaLabel="Contract template"
                    value={shapeIndex}
                    onChange={setShapeIndex}
                    options={creationShapes.map((item, index) => ({ value: String(index), label: item.template }))}
                />
                <Typography sx={{ color: colors.text.secondary, fontSize: '0.9rem', mt: 1.5, mb: 1.5 }}>{shape.summary}</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 12px', alignItems: 'center', mb: 2.5 }}>
                    <Typography sx={{ color: colors.text.muted, fontSize: '0.8rem' }}>signatory</Typography>
                    <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>{shape.signatories.map((party) => <PartyChip key={party} party={party} />)}</Box>
                    <Typography sx={{ color: colors.text.muted, fontSize: '0.8rem' }}>observer</Typography>
                    <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {shape.observers.length ? shape.observers.map((party) => <PartyChip key={party} party={party} />) : <Typography sx={{ color: colors.text.muted, fontSize: '0.85rem' }}>none</Typography>}
                    </Box>
                </Box>
                <MiniLabel>2. Who authorizes the create?</MiniLabel>
                <PartyPicker label="Authorizing parties" parties={['Alice', 'Bob', 'Bank', 'Registry']} selected={authorizers} onToggle={toggle} />
            </Panel>
            <Panel borderColor={created ? colors.accent.green : colors.accent.orange}>
                <MiniLabel>Ledger says</MiniLabel>
                {created ? (
                    <Verdict ok title={`${shape.template} created`}>
                        Every signatory ({shape.signatories.join(', ')}) authorized it, so the contract now exists.
                        It is delivered to {[...shape.signatories, ...shape.observers].join(', ')} and to nobody else.
                        {shape.observers.length > 0 && <> {shape.observers.join(', ')} sees it as an observer without ever having been asked.</>}
                    </Verdict>
                ) : (
                    <Verdict ok={false} title="Rejected: missing authorization">
                        {shape.template} names {missing.join(' and ')} as {missing.length > 1 ? 'signatories' : 'a signatory'}, and{' '}
                        {missing.length > 1 ? 'they have' : `${missing[0]} has`} not authorized this command. The contract never comes into existence.
                        {shape.signatories.length > 1 && <> A single submitter can never satisfy this alone: see the propose/accept pattern below.</>}
                    </Verdict>
                )}
            </Panel>
        </Box>
    );
};

const PartyIdAnatomy = () => {
    const colors = useColors();
    return (
        <Box sx={{
            my: 2.5,
            p: '16px',
            borderRadius: '8px',
            backgroundColor: colors.bg.tertiary,
            fontFamily: '"Share Tech Mono", monospace',
            fontSize: { xs: '0.88rem', md: '1.05rem' },
            wordBreak: 'break-all',
            textAlign: 'center',
        }}>
            <Box component="span" sx={{ color: colors.accent.purple }}>Alice</Box>
            <Box component="span" sx={{ color: colors.text.muted }}>::</Box>
            <Box component="span" sx={{ color: colors.accent.green }}>1220f2a9c4e1b7d38a…</Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: '24px', mt: 1.5, fontFamily: '"Exo 2", sans-serif', fontSize: '0.8rem', flexWrap: 'wrap' }}>
                <Box component="span" sx={{ color: colors.accent.purple }}>■ hint (a readable name)</Box>
                <Box component="span" sx={{ color: colors.accent.green }}>■ namespace fingerprint (whose key owns it)</Box>
            </Box>
        </Box>
    );
};

const PROPOSE_ACCEPT = `
template LoanProposal
  with
    bank : Party
    borrower : Party
    principal : Decimal
  where
    signatory bank          -- the bank commits to the offer
    observer borrower       -- the borrower can see it

    choice Accept : ContractId Loan
      controller borrower   -- only the borrower can accept
      do create Loan with ..  -- authorized by bank AND borrower

template Loan
  with
    bank : Party
    borrower : Party
    principal : Decimal
  where
    signatory bank, borrower  -- both are bound
`;

// This stage expects 3 quiz questions configured in the admin panel, in this order:
//   Q0 - what a signatory represents (mandatory agreement required to create a contract)
//   Q1 - parties as explicit named participants, contrasted with key-derived addresses
//   Q2 - whether one party can unilaterally impose an obligation on another
export const blocks: StageBlock[] = [
    content('intro', () => (
        <StageSection gradientBorder eyebrow="Stage 2 of 8" title="The world as contracts between parties">
            <Prose>
                Every network covered so far models the world differently. Ethereum and Solana track{' '}
                <strong>accounts</strong> with balances that get updated in place. Bitcoin tracks{' '}
                <strong>unspent outputs</strong>, coins that are spent whole and replaced by new ones. Canton models
                the world as <strong>contracts</strong> between explicitly named <strong>parties</strong>: each
                contract is a small record saying who is involved, what they've agreed to, and what can happen next.
            </Prose>
            <FactGrid columns={3} items={[
                ['Account (Ethereum, Solana)', 'A mutable balance or storage slot. A transfer edits two numbers in place.'],
                ['UTXO (Bitcoin)', 'An immutable coin. Spending destroys it and creates new outputs.'],
                ['Contract (Canton)', 'An immutable agreement between parties. Acting on it archives it and creates new contracts.'],
            ]} />
            <Prose>
                In that sense Canton is closer to Bitcoin than to Ethereum: a contract is never edited. It's{' '}
                <strong>active</strong> until something consumes it, then it's <strong>archived</strong> forever, and
                any "update" is really an archive plus a create. The set of all contracts that are still live is called
                the <strong>active contract set</strong> (ACS). The difference from a UTXO is what a contract carries:
                not just an amount, but a full agreement with named parties, data and rules.
            </Prose>
            <FactGrid items={[
                ['Template', 'The blueprint written in DAML: which fields a contract has, who must sign it, who may see it, and which actions (choices) it offers. Like a class.'],
                ['Contract', 'One concrete instance of a template with real parties and values, identified by a unique contract ID. Like an object.'],
                ['Party', 'A named participant in agreements, typically an institution (a bank, an exchange) or one of its users.'],
                ['Contract ID', 'A unique, unguessable identifier. Since contracts are immutable, a new version always gets a new ID.'],
            ]} />
        </StageSection>
    )),

    content('signatories', () => (
        <StageSection title="Signatories: mandatory, not optional">
            <Prose>
                Every template declares one or more <strong>signatories</strong>: the parties who are bound by the
                contract. The ledger enforces a hard rule: a contract can only be created if{' '}
                <em>every one</em> of its signatories authorized the transaction that creates it. Not most of them,
                not the one who submitted it: all of them. This is checked by the protocol on every participant
                involved, not left to the application or to good behavior.
            </Prose>
            <Prose>
                A template can also name <strong>observers</strong>, parties who are allowed to see the contract but
                aren't bound by it. Observers never need to authorize anything. Together, signatories and observers
                are the contract's <strong>stakeholders</strong>, the only parties the ledger ever shows it to.
            </Prose>
            <RoleTable />
            <Prose>
                Try it. Pick a template, then choose which parties authorize the create command. Notice what happens
                when the contract needs two signatories.
            </Prose>
            <CreationDemo />
        </StageSection>
    )),

    question(0),

    content('explicit-parties', () => (
        <StageSection title="Parties are named, not derived from a key">
            <Prose>
                On Bitcoin or Ethereum, an identity is an address: a pseudonymous string computed from a public key.
                Anyone can generate millions of them offline, nothing links one to a real organization, and whoever
                holds the private key <em>is</em> the account.
            </Prose>
            <Prose>
                A Canton <strong>party</strong> is an explicit identity registered on the network. Its ID pairs a
                human-readable hint with the fingerprint of the namespace key that owns it, and the network's shared
                topology state records which participant nodes host it:
            </Prose>
            <PartyIdAnatomy />
            <Prose>
                Because a party is tied to a namespace and to the nodes that host it, counterparties know exactly
                who they're dealing with. That matters for regulated business (you have to know your counterparty)
                and it's what makes explicit, named authorization possible in the first place.
            </Prose>
            <Callout tone="security" title="Security angle: who really acts for a party?">
                In the classic setup, a party doesn't hold a signing key of its own: its <strong>participant node</strong>{' '}
                submits and confirms transactions on its behalf. Whoever controls that node effectively controls every
                party it hosts. Canton also supports external parties that sign their own transactions, but either
                way, "who can act as Alice" is a question about keys and hosting, and a common thing to get wrong.
            </Callout>
        </StageSection>
    )),

    question(1),

    content('closing', () => (
        <StageSection title="Nothing is created unilaterally">
            <Prose>
                The signatory rule has a powerful consequence: <strong>no party can bind another without its
                consent</strong>. Bank can create an Iou where Bank itself owes Alice money, because Bank is the
                only signatory, and giving someone a claim needs no permission. But Bank can't create a Loan that
                obliges Alice to repay, because Alice would have to be a signatory, and only Alice can authorize
                that.
            </Prose>
            <Prose>
                So how do two parties ever end up on the same contract? One of them proposes, the other accepts, in
                two separate transactions:
            </Prose>
            <NumberedFlow steps={[
                ['Propose', 'Bank creates a LoanProposal. Bank is its only signatory, so Bank alone can create it; Alice is an observer, so Alice can see the offer.'],
                ['Accept', 'Alice exercises the Accept choice on the proposal. Only Alice is allowed to, because the template names her as its controller.'],
                ['Both bound', 'Inside Accept, the ledger combines the proposal signatory\'s authority (Bank) with the controller\'s (Alice), which is exactly enough to create a Loan signed by both.'],
            ]} />
            <CodeSample language="daml" caption="The propose/accept pattern" code={PROPOSE_ACCEPT} />
            <Callout tone="security" title="Where the bugs hide">
                The propose/accept pattern works by carrying authority from one contract into another. If an
                acceptance choice lets the acceptor pass in arbitrary arguments, or a proposal is broader than it
                looks, the combined authority can be used to create something the proposer never meant to sign.
                Mistakes in how authority flows are one of the most common classes of DAML vulnerability.
            </Callout>
        </StageSection>
    )),

    question(2),
];
